import type { FuelDoc } from './timing.ts'

/**
 * How fills read on screen, in one place so the tile and the page agree.
 *
 * The figures are the fuel job's; this only words them. "Laps" on a fill is the
 * laps the previous fuel lasted — the laps this fill replaced, which is what
 * the litres measure — and the fuel now in the car is its own line, because
 * that number is still growing.
 */

/** A car's fills, newest first. */
export function fillsFor(fills: readonly FuelDoc[], car: string | null): FuelDoc[] {
  if (!car) return []
  return fills.filter((f) => f.car === car).sort((a, b) => b.at.toMillis() - a.at.toMillis())
}

/** Where the fill went in. */
export function fillWhere(f: FuelDoc): string {
  switch (f.kind) {
    case 'stop':
      return f.stopNumber != null ? `stop ${f.stopNumber} · lap ${f.lap ?? '?'}` : `lap ${f.lap ?? '?'}`
    case 'after':
      return `after lap ${f.lap ?? '?'}`
    case 'before':
      return 'before the start'
    default:
      return 'not at a stop'
  }
}

/** What the fill says about consumption: laps since the last fuel, and per lap. */
export function fillUsage(f: FuelDoc): string | null {
  if (f.lapsSincePrevious == null || f.lapsSincePrevious <= 0) return null
  const laps = `${f.lapsSincePrevious} lap${f.lapsSincePrevious === 1 ? '' : 's'}${f.sinceStart ? ' from the start' : ''}`
  return f.litresPerLap != null ? `${laps} · ${f.litresPerLap.toFixed(2)} L/lap` : laps
}

/** How far this fill's fuel has gone, or went. */
export function fillLasted(f: FuelDoc): string | null {
  if (f.lapsOnFuel == null) return null
  const laps = `${f.lapsOnFuel} lap${f.lapsOnFuel === 1 ? '' : 's'}`
  return f.open ? `${laps} so far` : laps
}

export function formatLitres(l: number): string {
  return `${l.toFixed(1)} L`
}
